import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import MasterDashboard from "@/pages/MasterDashboard";
import RawMaterials from "@/pages/RawMaterials";
import RMInward from "@/pages/RMInwardShell";
import FGInward from "@/pages/FGInward";
import PendingForInvoiceReport from "@/pages/PendingForInvoiceReport";
import GrindingLog from "@/pages/GrindingLog";
import RMRepository from "@/pages/RMRepository";
// SKUs Legacy page removed - use SKU Management instead
// Dispatch Legacy page removed - use Dispatch Lots instead
// SKUMapping removed - use SKU Management BOM instead
import SKUSubscription from "@/pages/SKUSubscription";
import SKUManagement from "@/pages/SKUManagement";
import VendorManagement from "@/pages/VendorManagement";
import JobWorkBalances from "@/pages/JobWorkBalances";
import JobWorkReceipts from "@/pages/JobWorkReceipts";
import JobworkInvoices from "@/pages/JobworkInvoices";
// ProductionPlanning page removed - CPC handles all production planning from forecasts
// N5 (Feb 2026) — Production Planning exports now live on the existing
// Admin Reports page (MasterAdmin-only); no separate page or route.
// Production page removed - Branch Ops handles schedule completion
import DispatchLots from "@/pages/DispatchLots";
import BuyerManagement from "@/pages/BuyerManagement";
import Reports from "@/pages/Reports";
import UserManagement from "@/pages/UserManagement";
import RoleManagement from "@/pages/RoleManagement";
import DatabaseUpdates from "@/pages/DatabaseUpdates";
import CpcDashboard from "@/pages/CpcDashboard";
import BomLookup from "@/pages/BomLookup";
import TechOps from "@/pages/TechOps";
import CustomerDetail from "@/pages/CustomerDetail";
import Moulds from "@/pages/Moulds";
import Demand from "@/pages/Demand";
import ColourwayMock from "@/pages/ColourwayMock"; // mounted inside /colourway "Bidso SKU Composer" tab; no longer a standalone route
import Quality from "@/pages/Quality";
import CPC from "@/pages/CPC";
import Procurement from "@/pages/Procurement";
import Logistics from "@/pages/Logistics";
import LogisticsBookings from "@/pages/LogisticsBookings";
import IBT from "@/pages/IBT";
import Dispatch from "@/pages/Dispatch";
import DeliveryChallan from "@/pages/DeliveryChallan";
import Events from "@/pages/Events";
import BranchOps from "@/pages/BranchOps";
import DemandHub from "@/pages/DemandHub";
import DemandSKUView from "@/pages/DemandSKUView";
import ColorDevelopment from "@/pages/ColorDevelopment";
import Colourway from "@/pages/Colourway";
import RMShortage from "@/pages/RMShortage";
import RMRequired from "@/pages/RMRequired";
import RMProduction from "@/pages/RMProduction";
import FGProductionLog from "@/pages/FGProductionLog";
import Inventory from "@/pages/Inventory";
import InventoryMovement from "@/pages/InventoryMovement";
import InventoryImports from "@/pages/InventoryImports";
import DBExplorer from "@/pages/DBExplorer";
import Pulse from "@/pages/Pulse";
import Settings from "@/pages/Settings";
// Feb 2026 (MRP frontend rollout) — six routed pages under /mrp/*.
import MRPOverview from "@/pages/mrp/MRPOverview";
import MRPModelForecasts from "@/pages/mrp/MRPModelForecasts";
import MRPRMParams from "@/pages/mrp/MRPRMParams";
import MRPRuns from "@/pages/mrp/MRPRuns";
import MRPWeeklyPlan from "@/pages/mrp/MRPWeeklyPlan";
import MRPDraftPOs from "@/pages/mrp/MRPDraftPOs";
import PurchaseOrders from "@/pages/PurchaseOrders";
import { Toaster } from "@/components/ui/sonner";
import useAuthStore from "@/store/authStore";
import { hasAnyRole } from "@/utils/roleGuard";
import axios from "axios";
import { formatApiError } from "@/lib/apiError";
import "@/index.css";

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  return isAuthenticated ? children : <Navigate to="/login" replace />;
};

// Master Admin Only Route
const AdminRoute = ({ children }) => {
  const { isAuthenticated, user, userRoles } = useAuthStore();
  const isMasterAdmin = useAuthStore((state) => state.isMasterAdmin);
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  // Feb 2026 — align with the sidebar's `isMasterAdmin()` (checks BOTH
  // legacy `user.role == "master_admin"` AND `MASTER_ADMIN` in
  // `user_roles`). Formerly checked only the legacy field, so users
  // whose MASTER_ADMIN authority lived in `user_roles[]` saw the Admin
  // nav group but got bounced by this guard on click.
  if (!isMasterAdmin()) return <Navigate to="/dashboard" replace />;
  return children;
};

// Role-allowlist guard. Delegates the role-source-of-truth check to
// `utils/roleGuard.hasAnyRole`, which unions the legacy singular
// `user.role` with the new `userRoles[]` array and compares
// case-insensitively. The sidebar's `canSee()` resolver uses the same
// helper so route-level access matches sidebar visibility by construction.
const RoleRoute = ({ children, roles }) => {
  const { isAuthenticated, user, userRoles } = useAuthStore();
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (!hasAnyRole({ user, userRoles }, roles)) {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
};

// Entity-permission route guard. Mirrors the sidebar's `canSee({entity})`
// check so direct URL navigation matches sidebar visibility by construction.
// MASTER_ADMIN bypasses via `isMasterAdmin()`; every other user needs
// `hasPermission(entity, action)` (defaults to READ).
const EntityRoute = ({ children, entity, action = 'READ' }) => {
  const { isAuthenticated, hasPermission, isMasterAdmin } = useAuthStore();
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (isMasterAdmin()) return children;
  if (!hasPermission(entity, action)) return <Navigate to="/dashboard" replace />;
  return children;
};

// Set up axios interceptor for auth.
//
// Feb 2026 RCA fix: this must run at *module import time*, not inside
// App's useEffect. React commits child effects before parent effects,
// so pages hard-refreshed at their own URL (e.g. `/pulse`) previously
// dispatched their first `axios.get(...)` BEFORE App's effect installed
// the interceptor → request went out with no Authorization header →
// backend HTTPBearer returned 403 "Not authenticated". Installing at
// module scope kills the race for every page, not just Pulse.
const setupAxiosInterceptor = () => {
  axios.interceptors.request.use((config) => {
    const token = useAuthStore.getState().token;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response?.status === 401) {
        useAuthStore.getState().logout();
        window.location.href = '/login';
      }
      // Decorate every rejection with a precise, human-readable detail so
      // every existing `toast.error(error.response?.data?.detail || "...")`
      // catch block automatically renders something actionable instead of a
      // generic fallback. Idempotent — safe to call inside individual
      // handlers too via formatApiError().
      try {
        const fmt = formatApiError(error);
        if (!error.response) {
          // Network error / CORS / aborted — synthesize a response so the
          // existing fallback pattern picks up the formatted message.
          error.response = { status: 0, statusText: "Network error", data: { detail: fmt.message } };
        } else {
          if (!error.response.data || typeof error.response.data === "string") {
            error.response.data = { detail: fmt.message };
          } else if (
            error.response.data.detail === undefined ||
            error.response.data.detail === null ||
            Array.isArray(error.response.data.detail) ||
            (typeof error.response.data.detail === "object")
          ) {
            // Feb 2026 — preserve the original structured detail so page-level
            // handlers that switch on `detail.code` (e.g. 409 MOULD_CODE_EXISTS,
            // SUB_MOULD_INHERITS_FROM_PARENT, NO_MATCHING_COLOURANT) don't lose
            // it when we flatten to a toast-friendly string.
            error.originalDetail = error.response.data.detail;
            // If the detail is a structured error object with a `code` field,
            // keep the object AS-IS so `typeof detail === "object"` branches
            // in page handlers still work. Only flatten arrays (Pydantic 422)
            // and objects lacking a `code`.
            const d = error.response.data.detail;
            const isStructuredError = (
              d !== null && !Array.isArray(d) && typeof d === "object" &&
              typeof d.code === "string"
            );
            if (!isStructuredError) {
              // Replace array (Pydantic 422) / plain object / null with flattened string.
              error.response.data.detail = fmt.message;
            }
          }
        }
        error.formattedMessage = fmt.message;
        error.formattedToast = fmt.toast;
      } catch {
        // Decorating must never break the original rejection flow.
      }
      return Promise.reject(error);
    }
  );
};

// Install at module scope so it's ready before any page component mounts.
setupAxiosInterceptor();

function App() {
  return (
    <>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
            <Route index element={<Navigate to="/dashboard" replace />} />
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="master-dashboard" element={<AdminRoute><MasterDashboard /></AdminRoute>} />
            <Route path="user-management" element={<AdminRoute><UserManagement /></AdminRoute>} />
            <Route path="role-management" element={<AdminRoute><RoleManagement /></AdminRoute>} />
            <Route path="raw-materials" element={<RawMaterials />} />
            <Route path="inventory-imports" element={<InventoryImports />} />
            <Route path="rm-inward" element={<RMInward />} />
            <Route path="fg-inward" element={<FGInward />} />
            <Route path="pending-for-invoice" element={<PendingForInvoiceReport />} />
            <Route path="grinding-log" element={<GrindingLog />} />
            {/* SKUs Legacy removed - use SKU Management */}
            {/* sku-mapping route removed - use SKU Management BOM instead */}
            <Route path="sku-subscription" element={<SKUSubscription />} />
            <Route path="sku-management" element={<SKUManagement />} />
            <Route path="bom-lookup" element={<BomLookup />} />
            <Route path="vendors" element={<VendorManagement />} />
            <Route path="job-work-balances" element={<JobWorkBalances />} />
            <Route path="jobwork-receipts" element={<EntityRoute entity="ProductionBatch" action="CREATE"><JobWorkReceipts /></EntityRoute>} />
            <Route path="jobwork-invoices" element={<EntityRoute entity="JobworkInvoice" action="CREATE"><JobworkInvoices /></EntityRoute>} />
            {/* Production page removed - Branch Ops handles schedule completion */}
            {/* Dispatch Legacy removed - use Dispatch Lots */}
            <Route path="dispatch-lots" element={<DispatchLots />} />
            <Route path="buyer-management" element={<BuyerManagement />} />
            <Route path="reports" element={<Reports />} />
            <Route path="techops" element={<TechOps />} />
            <Route path="customers/:buyerId" element={<CustomerDetail />} />
            <Route path="moulds" element={<Moulds />} />
            <Route path="demand" element={<Demand />} />
            <Route path="quality" element={<Quality />} />
            <Route path="cpc" element={<CPC />} />
            <Route path="procurement" element={<Procurement />} />
            <Route path="procurement/purchase-orders" element={<EntityRoute entity="PurchaseOrder" action="READ"><PurchaseOrders /></EntityRoute>} />
            <Route path="logistics" element={<Logistics />} />
            <Route path="logistics-bookings" element={<LogisticsBookings />} />
            <Route path="ibt" element={<IBT />} />
            <Route path="delivery-challans" element={<DeliveryChallan />} />
            <Route path="dispatch" element={<Dispatch />} />
            <Route path="events" element={<Events />} />
            <Route path="branch-ops" element={<BranchOps />} />
            <Route path="rm-shortage" element={<RMShortage />} />
            <Route path="rm-required" element={<RMRequired />} />
            <Route path="rm-production" element={<RMProduction />} />
            <Route path="fg-production-log" element={<FGProductionLog />} />
            <Route path="rm-repository" element={<RMRepository />} />
            <Route path="inventory" element={<Inventory />} />
            <Route path="inventory-movement" element={<InventoryMovement />} />
            <Route path="demand-hub" element={<DemandHub />} />
            <Route path="demand-sku-view" element={<DemandSKUView />} />
            <Route path="color-development" element={<ColorDevelopment />} />
            <Route path="colourway" element={<Colourway />} />
            {/* DB Explorer is MASTER_ADMIN only (Jul 2026). TECHADMIN_VENDOR
                removed from the allowlist — vendors use /api/mongodb/* instead. */}
            <Route path="db-explorer" element={<RoleRoute roles={['MASTER_ADMIN']}><DBExplorer /></RoleRoute>} />
            {/* Company Pulse (Feb 2026) — RBAC via `Pulse:READ`
                permission entity. Replaces the previous hardcoded
                `['MASTER_ADMIN','master_admin']` role check. Any role
                granted `Pulse:READ` via the RoleManagement matrix
                (backend `role_permissions`) can now open this page
                with no code change. See `services/seed_rbac.py`. */}
            <Route path="pulse" element={<EntityRoute entity="Pulse" action="READ"><Pulse /></EntityRoute>} />
            {/* Feb 2026 (MRP frontend rollout) — six routed pages, guarded
                by the same role allowlist the sidebar + backend router use. */}
            <Route path="mrp" element={<RoleRoute roles={['MASTER_ADMIN', 'CPC_PLANNER', 'PROCUREMENT_OFFICER']}><MRPOverview /></RoleRoute>} />
            <Route path="mrp/model-forecasts" element={<RoleRoute roles={['MASTER_ADMIN', 'CPC_PLANNER', 'PROCUREMENT_OFFICER']}><MRPModelForecasts /></RoleRoute>} />
            <Route path="mrp/rm-params" element={<RoleRoute roles={['MASTER_ADMIN', 'CPC_PLANNER', 'PROCUREMENT_OFFICER']}><MRPRMParams /></RoleRoute>} />
            <Route path="mrp/runs" element={<RoleRoute roles={['MASTER_ADMIN', 'CPC_PLANNER', 'PROCUREMENT_OFFICER']}><MRPRuns /></RoleRoute>} />
            <Route path="mrp/weekly-plan" element={<RoleRoute roles={['MASTER_ADMIN', 'CPC_PLANNER', 'PROCUREMENT_OFFICER']}><MRPWeeklyPlan /></RoleRoute>} />
            <Route path="mrp/draft-pos" element={<RoleRoute roles={['MASTER_ADMIN', 'CPC_PLANNER', 'PROCUREMENT_OFFICER']}><MRPDraftPOs /></RoleRoute>} />
            <Route path="branch-mapping" element={<AdminRoute><Settings /></AdminRoute>} />
            <Route path="database-updates" element={<AdminRoute><DatabaseUpdates /></AdminRoute>} />
            <Route path="cpc-dashboard" element={<CpcDashboard />} />
          </Route>
        </Routes>
      </BrowserRouter>
      <Toaster />
    </>
  );
}

export default App;